import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { defineMessages } from 'react-intl.macro';
import { useFragment } from 'react-relay/hooks';

import { Button, Menu, MenuItem, Theme } from '@material-ui/core';
import { createStyles, withStyles, WithStyles } from '@material-ui/core/styles';
import Toolbar, { ToolbarProps } from '@material-ui/core/Toolbar';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import FilterListIcon from '@material-ui/icons/FilterList';
import { graphql } from 'babel-plugin-relay/macro';
import classnames from 'classnames';
import { includes } from 'lodash';

import { Badge, Checkbox, DialogChatActivityClose, IconButton, Typography } from 'components/v2';
import useReduxMutation from 'hooks/useReduxMutation';
import { actionChatActivityUnread } from 'sagas/sagaWatchersChatActivity';
import { defaultContextValues, useFilterMessagesContext } from 'contexts';

import { ToolbarChatActivities_Query$key } from './__generated__/ToolbarChatActivities_Query.graphql';
import TypographyChatActivityCount from './TypographyChatActivityCount';

const styles = ({ spacing: { unit } }: Theme) =>
  createStyles({
    root: {
      paddingLeft: 0,
      paddingRight: unit * 2,
    },
    iconButtonArrow: {
      padding: unit,
      marginRight: unit * 2,
    },
    typographyTotalChats: {
      flex: 1,
    },
    marginLeft: {
      marginLeft: unit * 2,
    },
  });

const messages = defineMessages({
  totalChats: {
    id: 'deprecated.fragments.ToolbarChatActivities.totalChats',
    defaultMessage: 'Total chats: ',
  },
  clear: {
    id: 'deprecated.fragments.ToolbarChatActivities.clear',
    defaultMessage: 'clear',
  },
  close: {
    id: 'deprecated.fragments.ToolbarChatActivities.close',
    defaultMessage: 'Close Chat',
  },
  bulkClose: {
    id: 'deprecated.fragments.ToolbarChatActivities.bulkClose',
    defaultMessage: 'Close Chats',
  },
  markAsUnread: {
    id: 'deprecated.fragments.ToolbarChatActivities.markAsUnread',
    defaultMessage: 'Mark as unread',
  },
});

export interface ToolbarChatActivitiesProps extends WithStyles<typeof styles>, Omit<ToolbarProps, 'classes'> {
  fragmentRef: ToolbarChatActivities_Query$key;
  onChatActivityAction?: (action: 'close' | 'unread', response: any) => void;
  idsChecked: string[];
  allChecked: boolean;
  filtersCount: number;
  includeActions: ('close' | 'unread')[];
  includeFilters: boolean;
  onSelectAll: (checked: boolean) => void;
  onFiltersOpen: () => void;
  onFiltersClear: () => void;
}

const ToolbarChatActivities: React.FC<ToolbarChatActivitiesProps> = ({
  classes,
  fragmentRef,
  idsChecked,
  allChecked,
  filtersCount,
  includeActions,
  includeFilters,
  onSelectAll,
  onFiltersOpen,
  onFiltersClear,
  onChatActivityAction = () => {},
  ...rest
}) => {
  const { setFilters: setFilterMessages } = useFilterMessagesContext();
  const data = useFragment<ToolbarChatActivities_Query$key>(
    graphql`
      fragment ToolbarChatActivities_Query on Query @argumentDefinitions(filter: { type: "FilterChatActivityInput" }) {
        ...TypographyChatActivityCount_Query @arguments(filter: $filter)
      }
    `,
    fragmentRef,
  );

  const { formatMessage } = useIntl();
  const [anchorEl, setAnchorEl] = useState<any>(null);
  const [open, setOpen] = useState(false);
  const [clear] = useReduxMutation(actionChatActivityUnread);
  const closeDisabled = !idsChecked.length || !includes(includeActions, 'close');
  const markAsUnreadDisabled = !idsChecked.length || !includes(includeActions, 'unread');

  const handleClose = () => {
    // WORKAROUND ensure we don't trigger the action in case the MenuItem is disabled
    // Adding a workaround for now till we can upgrade MUI https://github.com/mui-org/material-ui/issues/13464
    if (closeDisabled) {
      return;
    }
    setAnchorEl(null);
    setOpen(true);
  };

  const handleUnread = () => {
    if (markAsUnreadDisabled) {
      return;
    }
    setAnchorEl(null);
    clear(
      {
        method: 'put',
        url: `/chatActivities/unread?_id=${idsChecked}`,
        callbackSuccess: (response) => {
          onSelectAll(false);
          onChatActivityAction('unread', response);
        },
      },
      idsChecked,
    );
  };

  return (
    <>
      <Toolbar className={classes.root} {...rest}>
        {includeActions.length > 0 && (
          <>
            <Checkbox
              color="primary"
              size="small"
              checked={allChecked}
              onChange={(event, checked) => onSelectAll(checked)}
              inputProps={{ 'data-lh-id': 'check-all-chat-activities' }}
            />
            <IconButton
              className={classes.iconButtonArrow}
              color="primary"
              onClick={(event) => setAnchorEl(event.currentTarget)}
              data-lh-id="dropdown-chat-actions"
            >
              <ArrowDropDownIcon color="primary" />
            </IconButton>
          </>
        )}
        <Typography
          className={classnames(classes.typographyTotalChats, {
            [classes.marginLeft]: includeActions.length === 0,
          })}
        >
          {formatMessage(messages.totalChats)}
          <TypographyChatActivityCount fragmentRef={data} inline />
        </Typography>
        {filtersCount > 0 && (
          <Button
            color="primary"
            size="small"
            data-lh-id="clear-chat-filters-hub"
            onClick={() => {
              onFiltersClear();
              setFilterMessages(defaultContextValues.filters);
            }}
          >
            {formatMessage(messages.clear)}
          </Button>
        )}
        {includeFilters && (
          <Badge color="primary" size="small" badgeContent={filtersCount}>
            <IconButton data-lh-id="facilities-filter-hub" variant="square" color="primary" onClick={onFiltersOpen}>
              <FilterListIcon />
            </IconButton>
          </Badge>
        )}
      </Toolbar>
      <Menu
        id="ToolbarChatActivitiesMenu"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        <MenuItem data-data-lh-id="close-chat-hub" onClick={handleClose} disabled={closeDisabled}>
          {formatMessage(idsChecked.length > 1 ? messages.bulkClose : messages.close)}
        </MenuItem>
        <MenuItem data-data-lh-id="unread-chat-hub" onClick={handleUnread} disabled={markAsUnreadDisabled}>
          {formatMessage(messages.markAsUnread)}
        </MenuItem>
      </Menu>
      <DialogChatActivityClose
        ids={idsChecked}
        open={open}
        mutationConfig={{ callbackSuccess: (response: any) => onChatActivityAction('close', response) }}
        onClose={(success: boolean) => {
          setOpen(false);
          if (success) {
            onSelectAll(false);
          }
        }}
      />
    </>
  );
};

export default withStyles(styles)(ToolbarChatActivities);
